import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  WhatsappLogo, Spinner, CheckCircle, X, Sparkle, Phone, AddressBook,
  Check, Envelope, ArrowRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * PublishMatchModal — popup proattivo aperto subito dopo la pubblicazione
 * di un annuncio. Mostra i clienti compatibili, permette di selezionarli
 * con checkbox e di aprire WhatsApp con messaggi formali (Lei) già scritti,
 * uno alla volta, con avanzamento sequenziale.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   propertyId: string
 *   propertyTitle: string (opzionale, mostrato in header)
 *   shareLink: string (opzionale, link pubblico da appendere ai messaggi)
 */
export default function PublishMatchModal({ open, onClose, propertyId, propertyTitle, shareLink }) {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState({}); // contact_id -> bool
  const [sentIds, setSentIds] = useState({});   // contact_id -> bool
  const [activeIdx, setActiveIdx] = useState(-1); // index in selected list currently being sent
  const [view, setView] = useState("intro");    // intro | sending

  useEffect(() => {
    if (!open) return;
    setView("intro");
    setSentIds({});
    setActiveIdx(-1);
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/properties/${propertyId}/matches`);
        const list = data.matches || [];
        setMatches(list);
        // Pre-select all matches with phone (so they can receive WhatsApp).
        const sel = {};
        list.forEach((m) => { if (m.phone) sel[m.contact_id] = true; });
        setSelected(sel);
      } catch {
        toast.error("Calcolo match fallito");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, propertyId]);

  const toggle = (cid) => setSelected((s) => ({ ...s, [cid]: !s[cid] }));
  const selectedList = matches.filter((m) => selected[m.contact_id] && m.phone);
  const selectedCount = selectedList.length;

  const buildMsgWithLink = (m) => {
    const base = m.whatsapp_message || "";
    if (!shareLink) return base;
    return `${base}\n\nLink completo: ${shareLink}`;
  };

  const sendNext = (idx) => {
    const target = selectedList[idx];
    if (!target) return;
    const msg = encodeURIComponent(buildMsgWithLink(target));
    const phone = (target.phone || "").replace(/\D/g, "");
    const link = `https://wa.me/${phone}?text=${msg}`;
    window.open(link, "_blank", "noopener,noreferrer");
    setSentIds((s) => ({ ...s, [target.contact_id]: true }));
  };

  const startSequence = () => {
    if (selectedCount === 0) {
      toast.error("Seleziona almeno un cliente");
      return;
    }
    setView("sending");
    setActiveIdx(0);
    sendNext(0);
  };

  const advance = () => {
    const next = activeIdx + 1;
    if (next >= selectedList.length) {
      toast.success(`Hai aperto WhatsApp per ${selectedList.length} client${selectedList.length === 1 ? "e" : "i"}`);
      onClose();
      return;
    }
    setActiveIdx(next);
    sendNext(next);
  };

  const skip = () => {
    const next = activeIdx + 1;
    if (next >= selectedList.length) {
      onClose();
      return;
    }
    setActiveIdx(next);
    sendNext(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end lg:items-center justify-center p-3 lg:p-6" data-testid="publish-match-modal">
      <div className="w-full max-w-2xl bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E8E5DE] bg-gradient-to-br from-[#F5F4F0] to-white">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#7C8B6E] flex items-center justify-center shrink-0">
              <Sparkle size={16} weight="fill" color="#fff" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Annuncio pubblicato</div>
              <div className="text-[15px] font-semibold text-[#1C1C1A]">A chi potrebbe interessare?</div>
              {propertyTitle && (
                <div className="text-[11.5px] text-[#9A968B] mt-0.5 truncate max-w-[80vw] lg:max-w-[420px]">{propertyTitle}</div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#9A968B] hover:text-[#1C1C1A]" data-testid="publish-match-close" aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center" data-testid="publish-match-loading">
              <Spinner size={28} className="animate-spin mx-auto text-[#7C8B6E]" />
              <div className="mt-3 text-[13px] text-[#6B6A65]">Cerco clienti compatibili…</div>
            </div>
          ) : matches.length === 0 ? (
            <div className="py-10 px-6 text-center" data-testid="publish-match-empty">
              <AddressBook size={36} weight="duotone" className="text-[#C8C5BD] mx-auto mb-3" />
              <div className="text-[14px] font-semibold text-[#1C1C1A] mb-1">Nessun match al momento</div>
              <p className="text-[12.5px] text-[#6B6A65] leading-[1.55] mb-4 max-w-md mx-auto">
                Nessuno dei tuoi contatti ha preferenze compatibili con questo immobile. Aggiungi nuovi
                clienti con tipologia, zona e budget per avere match automatici al prossimo annuncio.
              </p>
              <Link
                to="/dashboard/contatti"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-4 py-2 rounded-md bg-[#1C1C1A] text-white hover:bg-black"
                data-testid="publish-match-add-contact"
              >
                Aggiungi clienti <ArrowRight size={13} weight="bold" />
              </Link>
            </div>
          ) : view === "intro" ? (
            <div className="px-5 py-4 space-y-3" data-testid="publish-match-list">
              <p className="text-[12.5px] text-[#6B6A65] leading-[1.5]">
                <strong className="text-[#1C1C1A]">{matches.length} client{matches.length === 1 ? "e" : "i"}</strong> compatibil{matches.length === 1 ? "e" : "i"} con questo annuncio.
                Seleziona chi vuoi avvisare. Pronto apre WhatsApp uno alla volta col messaggio già pronto (formale, dando del Lei).
              </p>
              <div className="space-y-1.5">
                {matches.map((m) => {
                  const isSel = !!selected[m.contact_id];
                  const noPhone = !m.phone;
                  return (
                    <label
                      key={m.contact_id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        noPhone
                          ? "border-[#E8E5DE] bg-[#FAFAF7] opacity-60 cursor-not-allowed"
                          : isSel
                            ? "border-[#7C8B6E] bg-[#F1F4ED]"
                            : "border-[#E8E5DE] bg-white hover:border-[#C8C5BD]"
                      }`}
                      data-testid={`publish-match-row-${m.contact_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        disabled={noPhone}
                        onChange={() => toggle(m.contact_id)}
                        className="mt-0.5 w-4 h-4 accent-[#7C8B6E] cursor-pointer disabled:cursor-not-allowed"
                        data-testid={`publish-match-checkbox-${m.contact_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13.5px] font-semibold text-[#1C1C1A]">{m.name}</span>
                          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7C8B6E]">{m.score}% match</span>
                          {m.phone ? (
                            <span className="text-[10.5px] text-[#9A968B] inline-flex items-center gap-0.5"><Phone size={10} /> {m.phone}</span>
                          ) : (
                            <span className="text-[10.5px] text-amber-700 inline-flex items-center gap-0.5"><Envelope size={10} /> solo email</span>
                          )}
                        </div>
                        {m.reasons?.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap text-[10.5px] text-[#7C8B6E] mt-0.5">
                            {m.reasons.map((r) => (
                              <span key={r} className="inline-flex items-center gap-0.5">
                                <CheckCircle size={9} weight="fill" /> {r}
                              </span>
                            ))}
                          </div>
                        )}
                        {m.whatsapp_message && (
                          <p className="mt-1.5 text-[11.5px] text-[#3F3D38] bg-white border-l-2 border-[#7C8B6E] pl-2 py-1 rounded-r italic leading-snug line-clamp-2">
                            "{m.whatsapp_message}"
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            // SENDING SEQUENCE
            <div className="px-5 py-5" data-testid="publish-match-sending">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold mb-1">
                {activeIdx + 1} di {selectedList.length}
              </div>
              {selectedList[activeIdx] && (
                <>
                  <div className="text-[16px] font-semibold text-[#1C1C1A] mb-0.5">
                    {selectedList[activeIdx].name}
                  </div>
                  <div className="text-[11.5px] text-[#9A968B] mb-3">
                    {selectedList[activeIdx].phone}
                  </div>
                  <div className="bg-[#F1F4ED] border-l-2 border-[#7C8B6E] p-3 rounded-r text-[12.5px] text-[#1C1C1A] leading-[1.55] whitespace-pre-wrap mb-4">
                    {buildMsgWithLink(selectedList[activeIdx])}
                  </div>
                  <p className="text-[11.5px] text-[#9A968B] leading-[1.5] mb-4">
                    WhatsApp si è aperto in una nuova scheda. Verifica il messaggio e premi "Invia" su WhatsApp.
                    Quando hai finito, torna qui e premi "Avanti" per il cliente successivo.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={skip}
                      className="py-2.5 border border-[#E8E5DE] bg-white rounded-md text-[12.5px] text-[#9A968B] hover:bg-[#FAFAF7] transition-colors"
                      data-testid="publish-match-skip"
                    >
                      Salta
                    </button>
                    <button
                      onClick={() => sendNext(activeIdx)}
                      className="py-2.5 border border-[#E8E5DE] bg-white rounded-md text-[12.5px] text-[#3F3D38] hover:bg-[#FAFAF7] transition-colors inline-flex items-center justify-center gap-1.5"
                      data-testid="publish-match-reopen"
                    >
                      <WhatsappLogo size={13} weight="fill" className="text-[#25D366]" /> Riapri
                    </button>
                    <button
                      onClick={advance}
                      className="py-2.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white font-medium rounded-md text-[12.5px] inline-flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="publish-match-advance"
                    >
                      {activeIdx + 1 >= selectedList.length ? <>Fine <Check size={13} weight="bold" /></> : <>Avanti <ArrowRight size={13} weight="bold" /></>}
                    </button>
                  </div>
                  {/* Progress dots */}
                  <div className="flex items-center justify-center gap-1 mt-4" aria-hidden>
                    {selectedList.map((_, i) => (
                      <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          i < activeIdx ? "bg-[#7C8B6E]"
                          : i === activeIdx ? "bg-[#1C1C1A]"
                          : "bg-[#E8E5DE]"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer (intro view only) */}
        {!loading && matches.length > 0 && view === "intro" && (
          <div className="border-t border-[#E8E5DE] px-5 py-3 bg-white">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11.5px] text-[#9A968B]">
                {selectedCount} di {matches.length} selezionat{selectedCount === 1 ? "o" : "i"}
                {Object.keys(sentIds).length > 0 && ` · ${Object.keys(sentIds).length} aperti`}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-[12.5px] text-[#9A968B] hover:text-[#1C1C1A]"
                  data-testid="publish-match-later"
                >
                  Vedo dopo
                </button>
                <button
                  onClick={startSequence}
                  disabled={selectedCount === 0}
                  className="px-4 py-2.5 bg-[#25D366] hover:bg-[#1FB855] disabled:opacity-50 text-white font-semibold rounded-md text-[12.5px] inline-flex items-center gap-1.5 transition-colors"
                  data-testid="publish-match-send-all"
                >
                  <WhatsappLogo size={14} weight="fill" />
                  Manda WhatsApp a {selectedCount || 0}
                </button>
              </div>
            </div>
            <div className="text-[10.5px] text-[#9A968B] mt-2 leading-[1.45]">
              Pronto crea anche un promemoria in <span className="font-semibold text-[#7C8B6E]">Agenda</span> così se chiudi questa finestra ritrovi tutti i match più tardi.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
